import { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button, Field, FieldLabel, Input, InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from '@clouway/ui'

import { UnsavedChangesGuard } from '@/components/UnsavedChangesGuard'
import { useSession } from '@/features/auth'
import type { ServiceAccountInput } from '../types'

// Mirrors the backend's email composition for a new service account
// (`${emailLocalPart}@${callerNamespace}${EMAIL_DOMAIN_SUFFIX}`, verbatim
// concatenation with no server-side normalization — see PgServiceAccounts.kt
// and the suffix constant wired in AccountsBootstrap). Keep this in sync if
// the backend suffix ever changes.
const EMAIL_DOMAIN_SUFFIX = '.apps.telcongserviceaccount.com'

// The backend does not normalize the local part it is given, so the form
// validates the allowed charset up front instead of silently rewriting it.
const LOCAL_PART_PATTERN = /^[a-z0-9-]+$/

const buildSchema = (creating: boolean, requiredMessage: string, invalidLocalPartMessage: string) =>
  z.object({
    name: z.string().min(1, requiredMessage),
    // Only meaningful on create — edit mode never renders the field, so it
    // must stay unconstrained there or the hidden default value would block
    // submission.
    emailLocalPart: creating
      ? z.string().min(1, requiredMessage).regex(LOCAL_PART_PATTERN, invalidLocalPartMessage)
      : z.string(),
  })

type ServiceAccountFormValues = z.infer<ReturnType<typeof buildSchema>>

export function ServiceAccountForm({
  mode,
  initialValues,
  clientEmail,
  onSubmit,
}: {
  mode: 'create' | 'edit'
  initialValues?: ServiceAccountInput
  // The account's final email, shown read-only once it exists (create mode
  // only previews the local part — the domain is appended server-side).
  clientEmail?: string
  onSubmit: (input: ServiceAccountInput) => void | Promise<void>
}) {
  const { t } = useTranslation('feature')
  const { user } = useSession()
  const creating = mode === 'create'
  const schema = useMemo(
    () => buildSchema(creating, t('serviceAccount.form.required'), t('serviceAccount.form.invalidLocalPart')),
    [creating, t],
  )
  const { control, handleSubmit, formState } = useForm<ServiceAccountFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initialValues?.name ?? '',
      emailLocalPart: initialValues?.emailLocalPart ?? '',
    },
  })
  // The caller's namespace is the session's tenantId — the same value the
  // backend resolves server-side for the authenticated caller.
  const domain = `${user?.tenantId ?? ''}${EMAIL_DOMAIN_SUFFIX}`

  return (
    <form className="mx-auto flex w-full max-w-2xl flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      {/* isSubmitSuccessful only flips true AFTER onSubmit resolves, but this
          screen navigates away from INSIDE onSubmit — so without also
          checking isSubmitting the guard would still be armed (and intercept
          that navigation) at the moment it fires. RHF sets isSubmitting=true
          and re-renders before invoking onSubmit, so the guard is already
          disarmed by the time onSubmit's navigate() runs; a failed submit
          flips isSubmitting back to false and re-arms it. */}
      <UnsavedChangesGuard when={formState.isDirty && !formState.isSubmitting && !formState.isSubmitSuccessful} />

      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel htmlFor="sa-name">{t('serviceAccount.form.name')}</FieldLabel>
            <Input id="sa-name" {...field} />
            {fieldState.error && <span className="text-destructive text-sm">{fieldState.error.message}</span>}
          </Field>
        )}
      />

      {creating && (
        <Controller
          control={control}
          name="emailLocalPart"
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel htmlFor="sa-email-local">{t('serviceAccount.form.emailLocalPart')}</FieldLabel>
              <InputGroup>
                <InputGroupInput id="sa-email-local" aria-invalid={!!fieldState.error} {...field} />
                <InputGroupAddon align="inline-end" className="max-w-[45%]">
                  <InputGroupText className="truncate" title={`@${domain}`}>
                    @{domain}
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
              {fieldState.error && <span className="text-destructive text-sm">{fieldState.error.message}</span>}
            </Field>
          )}
        />
      )}

      {!creating && clientEmail && (
        <Field>
          <FieldLabel htmlFor="sa-email">{t('serviceAccount.form.email')}</FieldLabel>
          <p id="sa-email" className="border-input bg-muted/30 rounded-md border px-3 py-2 font-mono text-sm break-all">
            {clientEmail}
          </p>
        </Field>
      )}

      <Button type="submit" disabled={formState.isSubmitting}>
        {formState.isSubmitting
          ? t(creating ? 'serviceAccount.form.creating' : 'serviceAccount.form.submitting')
          : t(creating ? 'serviceAccount.form.create' : 'serviceAccount.form.submit')}
      </Button>
    </form>
  )
}
